import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BuildingLibraryIcon,
  ChartBarIcon,
  UsersIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  SunIcon,
  MoonIcon,
  ChartPieIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  BellAlertIcon,
  LockClosedIcon,
  ChevronDownIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  FolderIcon,
  ServerIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PresentationChartLineIcon,
  TableCellsIcon,
  ArchiveBoxIcon,
  SparklesIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../../contextos/autenticacion/ContextoAutenticacion'
import { useTema } from '../../contextos/tema/ContextoTema'
import { usePermisos } from '../../ganchos/usePermisos'

interface LayoutAdminProps {
  children: React.ReactNode
}

interface SubMenuItem {
  name: string
  href: string
  icon: React.ComponentType<any>
  description?: string
  permiso?: string
}

interface MenuItem {
  name: string
  href?: string
  icon: React.ComponentType<any>
  current?: boolean
  subItems?: SubMenuItem[]
  description?: string
  permiso?: string
}

const LayoutAdmin: React.FC<LayoutAdminProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['Dashboard'])
  const { usuario, logout } = useAuth()
  const { toggleTema, isDark } = useTema()
  const { tienePermiso } = usePermisos()
  const location = useLocation()
  const navigate = useNavigate()

  const adminNavigation: MenuItem[] = [
    { 
      name: 'Dashboard Admin', 
      href: '/admin/dashboard', 
      icon: ChartBarIcon,
      description: 'Panel principal de administración',
      permiso: 'dashboard:ver'
    },
    { 
      name: 'Normatividad', 
      icon: BuildingLibraryIcon,
      description: 'Administrar base de datos legal',
      permiso: 'normatividad:ver',
      subItems: [
        { name: 'Gestión', href: '/admin/normatividad', icon: CogIcon, description: 'Gestionar normatividad', permiso: 'normatividad:ver' },
        { name: 'Buscador LIGIE', href: '/admin/tarifas/clasificacion', icon: MagnifyingGlassIcon, description: 'Buscar en LIGIE', permiso: 'normatividad:ver' }
      ]
    },
    {
      name: 'Gestión de Usuarios',
      icon: UsersIcon,
      description: 'Administrar usuarios del sistema',
      permiso: 'usuarios:ver',
      subItems: [
        { name: 'Todos los Usuarios', href: '/admin/usuarios', icon: UsersIcon, description: 'Ver y gestionar todos los usuarios', permiso: 'usuarios:ver' },
        { name: 'Roles y Permisos', href: '/admin/usuarios/roles', icon: ShieldCheckIcon, description: 'Configurar roles y permisos', permiso: 'roles:ver' },
        { name: 'Usuarios Activos', href: '/admin/usuarios/activos', icon: EyeIcon, description: 'Monitorear usuarios activos', permiso: 'usuarios:ver' }
      ]
    },
    {
      name: 'Gestión de Instituciones',
      href: '/admin/instituciones',
      icon: BuildingOfficeIcon,
      description: 'Administrar universidades y empresas',
      permiso: 'instituciones:ver'
    },
    {
      name: 'Gestión de Contenido',
      icon: FolderIcon,
      description: 'Administrar contenido del sistema',
      permiso: 'contenido:ver',
      subItems: [
        { name: 'Categorías', href: '/admin/categorias', icon: TableCellsIcon, description: 'Gestionar categorías de productos', permiso: 'categorias:ver' },
        { name: 'Archivos', href: '/admin/archivos', icon: ArchiveBoxIcon, description: 'Administrar archivos del sistema', permiso: 'archivos:ver' },
        { name: 'Documentos', href: '/admin/documentos', icon: DocumentTextIcon, description: 'Gestionar documentos', permiso: 'documentos:ver' }
      ]
    },
    {
      name: 'Reportes y Analíticas',
      icon: ChartPieIcon,
      description: 'Informes y análisis del sistema',
      permiso: 'reportes:ver',
      subItems: [
        { name: 'Reportes Generales', href: '/admin/reportes', icon: PresentationChartLineIcon, description: 'Reportes generales del sistema', permiso: 'reportes:ver' },
        { name: 'Analíticas de Uso', href: '/admin/reportes/analiticas', icon: ChartBarIcon, description: 'Análisis de uso del sistema', permiso: 'analiticas:ver' },
        { name: 'Reportes Financieros', href: '/admin/reportes/financieros', icon: ClipboardDocumentListIcon, description: 'Informes financieros', permiso: 'financieros:ver' }
      ]
    },
    {
      name: 'Configuración del Sistema',
      icon: CogIcon,
      description: 'Configuraciones avanzadas',
      permiso: 'configuracion:ver',
      subItems: [
        { name: 'Configuración General', href: '/admin/configuracion', icon: CogIcon, description: 'Configuraciones generales del sistema', permiso: 'configuracion:ver' },
        { name: 'Seguridad', href: '/admin/configuracion/seguridad', icon: LockClosedIcon, description: 'Configuraciones de seguridad', permiso: 'seguridad:ver' },
        { name: 'Notificaciones', href: '/admin/configuracion/notificaciones', icon: BellAlertIcon, description: 'Configurar notificaciones del sistema', permiso: 'notificaciones:ver' },
        { name: 'Respaldos', href: '/admin/configuracion/respaldos', icon: ServerIcon, description: 'Gestionar respaldos del sistema', permiso: 'respaldos:ver' }
      ]
    },
    {
      name: 'Monitoreo',
      icon: ExclamationTriangleIcon,
      description: 'Monitoreo del sistema',
      permiso: 'monitoreo:ver',
      subItems: [
        { name: 'Estado del Sistema', href: '/admin/monitoreo/estado', icon: ServerIcon, description: 'Estado actual del sistema', permiso: 'monitoreo:ver' },
        { name: 'Auditoría Admin', href: '/admin/monitoreo/logs', icon: DocumentTextIcon, description: 'Bitácora de eventos sensibles y administrativos', permiso: 'logs:ver' },
        { name: 'Alertas', href: '/admin/monitoreo/alertas', icon: ExclamationTriangleIcon, description: 'Alertas del sistema', permiso: 'alertas:ver' }
      ]
    }
  ]

  // Filtrar navegación basada en permisos
  const filteredNavigation = adminNavigation.filter(item => {
    // Si no tiene permiso definido, mostrar por defecto (o ocultar si queremos ser estrictos)
    // Asumiremos que si no hay permiso definido, es público para admins
    if (!item.permiso) return true
    
    // Verificar permiso principal
    const tienePermisoPrincipal = tienePermiso(item.permiso)
    
    // Si tiene subitems, verificar si tiene acceso a al menos uno
    if (item.subItems) {
       // Filtrar subitems válidos
       const subItemsValidos = item.subItems.filter(subItem => !subItem.permiso || tienePermiso(subItem.permiso))
       // Si no tiene subitems válidos y el padre requiere permiso, ocultar
       if (subItemsValidos.length === 0) return false
       
       // Si tiene permiso principal, pero filtramos los subitems para renderizar
       return tienePermisoPrincipal
    }
    
    return tienePermisoPrincipal
  })

  const toggleMenu = (menuName: string) => {
    setExpandedMenus(prev => 
      prev.includes(menuName) 
        ? prev.filter(name => name !== menuName)
        : [...prev, menuName]
    )
  }

  const isMenuExpanded = (menuName: string) => {
    return expandedMenus.includes(menuName)
  }

  const isCurrentPath = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Error al cerrar sesión:', error)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300">
      {/* Sidebar móvil */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              initial={{ x: -320, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -320, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 z-50 w-80 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl shadow-2xl border-r border-gray-200/50 dark:border-gray-700/50 lg:hidden"
            >
              {/* Header móvil mejorado */}
              <div className="flex h-20 items-center justify-between px-6 bg-gradient-to-r from-red-600 via-red-700 to-red-800 shadow-lg">
                <motion.div 
                  className="flex items-center"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                    <SparklesIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <span className="text-xl font-bold text-white tracking-tight">LogiXport</span>
                    <div className="text-sm text-red-100/80 font-medium">Panel Admin</div>
                  </div>
                </motion.div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSidebarOpen(false)}
                  className="p-2.5 rounded-xl text-red-100 hover:text-white hover:bg-red-600/30 transition-all duration-200"
                >
                  <XMarkIcon className="h-6 w-6" />
                </motion.button>
              </div>

              {/* Navegación móvil mejorada */}
              <nav className="mt-6 px-4 pb-4 overflow-y-auto h-full">
                <div className="space-y-2">
                  {filteredNavigation.map((item, index) => (
                    <motion.div 
                      key={item.name}
                      initial={{ x: -50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      {item.href ? (
                        <Link
                          to={item.href}
                          onClick={() => setSidebarOpen(false)}
                          className={`${
                            isCurrentPath(item.href)
                              ? 'bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 shadow-md'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-700/50 dark:hover:to-gray-600/30 hover:text-gray-900 dark:hover:text-white'
                          } group flex items-center px-4 py-4 text-sm font-medium rounded-xl transition-all duration-200 transform hover:scale-[1.02]`}
                        >
                          <div className={`${
                            isCurrentPath(item.href) ? 'bg-red-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:bg-red-100 dark:group-hover:bg-red-900/30 group-hover:text-red-600'
                          } p-2.5 rounded-lg mr-4 transition-all duration-200`}>
                            <item.icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold">{item.name}</div>
                            {item.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{item.description}</div>
                            )}
                          </div>
                        </Link>
                      ) : (
                        <>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => toggleMenu(item.name)}
                            className="w-full group flex items-center justify-between px-4 py-4 text-sm font-medium rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-700/50 dark:hover:to-gray-600/30 hover:text-gray-900 dark:hover:text-white transition-all duration-200"
                          >
                            <div className="flex items-center">
                              <div className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:bg-red-100 dark:group-hover:bg-red-900/30 group-hover:text-red-600 p-2.5 rounded-lg mr-4 transition-all duration-200">
                                <item.icon className="h-5 w-5" />
                              </div>
                              <div className="text-left flex-1">
                                <div className="font-semibold">{item.name}</div>
                                {item.description && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{item.description}</div>
                                )}
                              </div>
                            </div>
                            <motion.div
                              animate={{ rotate: isMenuExpanded(item.name) ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                            </motion.div>
                          </motion.button>
                          <AnimatePresence>
                            {isMenuExpanded(item.name) && item.subItems && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                className="overflow-hidden"
                              >
                                <div className="ml-8 mt-2 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-4">
                                  {item.subItems.map((subItem, subIndex) => (
                                    <motion.div
                                      key={subItem.name}
                                      initial={{ x: -20, opacity: 0 }}
                                      animate={{ x: 0, opacity: 1 }}
                                      transition={{ delay: subIndex * 0.05 }}
                                    >
                                      <Link
                                        to={subItem.href}
                                        onClick={() => setSidebarOpen(false)}
                                        className={`${
                                          isCurrentPath(subItem.href)
                                            ? 'bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 shadow-sm'
                                            : 'text-gray-600 dark:text-gray-400 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-700/30 dark:hover:to-gray-600/20 hover:text-gray-900 dark:hover:text-white'
                                        } group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02]`}
                                      >
                                        <div className={`${
                                          isCurrentPath(subItem.href) ? 'bg-red-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 group-hover:bg-red-100 dark:group-hover:bg-red-900/30 group-hover:text-red-600'
                                        } p-2 rounded-lg mr-3 transition-all duration-200`}>
                                          <subItem.icon className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1">
                                          <div className="font-medium">{subItem.name}</div>
                                          {subItem.description && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{subItem.description}</div>
                                          )}
                                        </div>
                                      </Link>
                                    </motion.div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      )}
                    </motion.div>
                  ))}
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Sidebar desktop mejorado */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-80 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-r border-gray-200/50 dark:border-gray-700/50 shadow-xl">
          {/* Header desktop mejorado */}
          <div className="flex h-20 flex-shrink-0 items-center px-6 bg-gradient-to-r from-red-600 via-red-700 to-red-800 shadow-lg">
            <motion.div 
              className="flex items-center"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                <SparklesIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-3">
                <span className="text-xl font-bold text-white tracking-tight">LogiXport</span>
                <div className="text-sm text-red-100/80 font-medium">Panel de Administración</div>
              </div>
            </motion.div>
          </div>

          {/* Navegación desktop mejorada */}
          <div className="flex flex-1 flex-col overflow-y-auto">
            <nav className="flex-1 px-4 py-6">
              <div className="space-y-2">
                {filteredNavigation.map((item, index) => (
                  <motion.div 
                    key={item.name}
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {item.href ? (
                      <Link
                        to={item.href}
                        className={`${
                          isCurrentPath(item.href)
                            ? 'bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 shadow-md'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-700/50 dark:hover:to-gray-600/30 hover:text-gray-900 dark:hover:text-white'
                        } group flex items-center px-4 py-4 text-sm font-medium rounded-xl transition-all duration-200 transform hover:scale-[1.02]`}
                      >
                        <div className={`${
                          isCurrentPath(item.href) ? 'bg-red-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:bg-red-100 dark:group-hover:bg-red-900/30 group-hover:text-red-600'
                        } p-2.5 rounded-lg mr-4 transition-all duration-200`}>
                          <item.icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold">{item.name}</div>
                          {item.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{item.description}</div>
                          )}
                        </div>
                      </Link>
                    ) : (
                      <>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => toggleMenu(item.name)}
                          className="w-full group flex items-center justify-between px-4 py-4 text-sm font-medium rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-700/50 dark:hover:to-gray-600/30 hover:text-gray-900 dark:hover:text-white transition-all duration-200"
                        >
                          <div className="flex items-center">
                            <div className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:bg-red-100 dark:group-hover:bg-red-900/30 group-hover:text-red-600 p-2.5 rounded-lg mr-4 transition-all duration-200">
                              <item.icon className="h-5 w-5" />
                            </div>
                            <div className="text-left flex-1">
                              <div className="font-semibold">{item.name}</div>
                              {item.description && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{item.description}</div>
                              )}
                            </div>
                          </div>
                          <motion.div
                            animate={{ rotate: isMenuExpanded(item.name) ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                          </motion.div>
                        </motion.button>
                        <AnimatePresence>
                          {isMenuExpanded(item.name) && item.subItems && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="ml-8 mt-2 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-4">
                                {item.subItems.map((subItem, subIndex) => (
                                  <motion.div
                                    key={subItem.name}
                                    initial={{ x: -20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: subIndex * 0.05 }}
                                  >
                                    <Link
                                      to={subItem.href}
                                      className={`${
                                        isCurrentPath(subItem.href)
                                          ? 'bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 shadow-sm'
                                          : 'text-gray-600 dark:text-gray-400 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-700/30 dark:hover:to-gray-600/20 hover:text-gray-900 dark:hover:text-white'
                                      } group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02]`}
                                    >
                                      <div className={`${
                                        isCurrentPath(subItem.href) ? 'bg-red-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 group-hover:bg-red-100 dark:group-hover:bg-red-900/30 group-hover:text-red-600'
                                      } p-2 rounded-lg mr-3 transition-all duration-200`}>
                                        <subItem.icon className="h-4 w-4" />
                                      </div>
                                      <div className="flex-1">
                                        <div className="font-medium">{subItem.name}</div>
                                        {subItem.description && (
                                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{subItem.description}</div>
                                        )}
                                      </div>
                                    </Link>
                                  </motion.div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    )}
                  </motion.div>
                ))}
              </div>
            </nav>
          </div>
        </div>
      </div>

      {/* Contenido principal mejorado */}
      <div className="lg:pl-80 flex flex-col flex-1">
        {/* Header mejorado */}
        <motion.div 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="sticky top-0 z-30 flex h-20 flex-shrink-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl shadow-lg border-b border-gray-200/50 dark:border-gray-700/50"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="button"
            className="border-r border-gray-200/50 dark:border-gray-700/50 px-6 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500 lg:hidden transition-all duration-200"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </motion.button>
          
          <div className="flex flex-1 justify-between px-6">
            <div className="flex flex-1">
              <div className="flex items-center">
                <motion.div 
                  className="flex items-center space-x-3"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                    <SparklesIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                       {adminNavigation.find(item => item.href && isCurrentPath(item.href))?.name || 
                        adminNavigation.find(item => item.subItems?.some(subItem => isCurrentPath(subItem.href)))?.name ||
                        'Panel de Administración'}
                     </h1>
                     <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-medium hidden sm:block">
                       {adminNavigation.find(item => item.href && isCurrentPath(item.href))?.description || 
                        adminNavigation.find(item => item.subItems?.some(subItem => isCurrentPath(subItem.href)))?.description ||
                        'Gestión y control del sistema'}
                     </div>
                  </div>
                </motion.div>
              </div>
            </div>
            
            <motion.div 
              className="ml-4 flex items-center md:ml-6 space-x-3"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {/* Toggle de tema mejorado */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={toggleTema}
                className="relative p-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-all duration-200 shadow-md hover:shadow-lg"
                title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              >
                <AnimatePresence mode="wait">
                  {isDark ? (
                    <motion.div
                      key="sun"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <SunIcon className="h-5 w-5" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="moon"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <MoonIcon className="h-5 w-5" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>

              {/* Notificaciones mejoradas */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                type="button"
                className="relative p-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-all duration-200 shadow-md hover:shadow-lg"
                title="Notificaciones"
              >
                <BellIcon className="h-5 w-5" />
                <motion.div 
                  className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </motion.button>

              {/* Menú de usuario mejorado */}
              <motion.div 
                className="relative ml-3"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center space-x-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-xl px-4 py-2 shadow-md">
                  <div className="hidden md:block text-right">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {usuario?.nombre || 'Administrador'}
                    </div>
                    <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                      Administrador del Sistema
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                      <ShieldCheckIcon className="h-6 w-6 text-white" />
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleLogout}
                      className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                      title="Cerrar sesión"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">Salir</span>
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Contenido de la página mejorado */}
         <main className="flex-1 min-h-0 relative">
           <motion.div 
             className="h-full py-4 sm:py-6 lg:py-8"
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             transition={{ delay: 0.3 }}
           >
             <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 h-full">
               <motion.div 
                 className="h-full bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-lg lg:shadow-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
                 initial={{ scale: 0.95, opacity: 0 }}
                 animate={{ scale: 1, opacity: 1 }}
                 transition={{ delay: 0.4 }}
               >
                 <div className="h-full p-4 sm:p-6 lg:p-8">
                   {children}
                 </div>
               </motion.div>
             </div>
           </motion.div>
         </main>

        {/* Footer mejorado */}
        <motion.footer 
          className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-t border-gray-200/50 dark:border-gray-700/50 mt-auto shadow-lg"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10">
             <div className="py-4 sm:py-6">
               <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                 <motion.div 
                   className="flex items-center space-x-2 sm:space-x-3"
                   whileHover={{ scale: 1.02 }}
                 >
                   <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-md">
                     <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                   </div>
                   <div>
                     <span className="text-sm font-bold text-gray-900 dark:text-white">
                       LogiXport
                     </span>
                     <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                       Panel de Administración
                     </div>
                   </div>
                 </motion.div>
                 <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 flex items-center space-x-2 text-center sm:text-right">
                   <span>© {new Date().getFullYear()} LogiXport.</span>
                   <span className="hidden md:inline">
                     Powered by <a href="https://vespex.pro" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-500 dark:text-cyan-400 transition-colors font-bold">VESPEX PRO</a>
                   </span>
                   <motion.div
                     className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"
                     animate={{ scale: [1, 1.2, 1] }}
                     transition={{ duration: 2, repeat: Infinity }}
                     title="Sistema en línea"
                   />
                 </div>
               </div>
             </div>
           </div>
        </motion.footer>
      </div>
    </div>
  )
}

export default LayoutAdmin
